import $api from "@/lib/queries/openapi";
import Select from "react-select";
import useOperationContext from "./lib/useOperationContext";
import classnames from "classnames";
import { setIn, getIn } from "@/lib/utils";

export default function MaterialNameSelect({
  keyPath = ["material_name"],
  width = 140,
}: {
  keyPath?: string[];
  width?: number;
}) {
  const { operationData: data, updateOperation } = useOperationContext();
  const { id, draft, validation_errors, augmented } = data;
  const draftMaterialName = getIn(draft, keyPath, "") as string;
  const augmentedMaterialName = getIn(augmented, keyPath, "") as string;
  const materialName = draftMaterialName || augmentedMaterialName;
  const invalid = !!getIn(validation_errors, keyPath, null);
  const { data: materials = [] } = $api.useQuery("get", "/api/materials");

  const getShortName = (name: string) => {
    const material = materials.find(
      ({ type, alloy }) => name === `${type} ${alloy}`,
    );

    if (material) {
      if (material.type.startsWith("Plastic")) {
        return material.alloy;
      }
      return `${material.short_name} ${material.alloy}`;
    }

    return name;
  };

  return (
    <div
      className={classnames(
        "h-7.5 px-1 pb-1 mt-1 flex flex-col justify-center items-start text-sm text-gray-800 rounded-sm",
        {
          "border border-red-600": invalid,
          "border border-transparent focus-within:border focus-within:border-blue-600":
            !invalid,
        },
      )}
      style={{ width }}
    >
      <div className="px-1 text-xs text-gray-400 mt-[-8px] bg-white">
        Material
      </div>
      <div
        className={classnames("w-full flex", {
          "text-blue-600": !!draftMaterialName,
        })}
        title={materialName}
      >
        <Select
          tabIndex={invalid ? 0 : -1}
          unstyled
          classNames={{
            control: () => "!min-h-5",
            menu: () => "bg-white p-1 text-gray-800 text-xs rounded-md my-2",
            option: () => "p-1 hover:bg-gray-200",
          }}
          styles={{
            container: (base) => ({ ...base, width }),
            indicatorSeparator: () => ({ visibility: "hidden" }),
            indicatorsContainer: () => ({ visibility: "hidden" }),
            menu: (base) => ({
              ...base,
              boxShadow:
                "0 0 0 1px hsla(0, 0%, 0%, 0.1),0 4px 11px hsla(0, 0%, 0%, 0.1)",
            }),
          }}
          placeholder="Material"
          options={materials.map(({ type, alloy }) => ({
            label: getShortName(`${type} ${alloy}`),
            value: `${type} ${alloy}`,
          }))}
          onChange={(option) => {
            if (!option) return;
            updateOperation(id, setIn(draft, keyPath, option.value));
          }}
          filterOption={(option, searchText) => {
            const searchTextOptions = searchText.split(/\s+/);
            return searchTextOptions.every((search) =>
              option.data.label.toLowerCase().includes(search.toLowerCase()),
            );
          }}
          value={{
            label: getShortName(materialName),
            value: materialName,
          }}
          menuPlacement="bottom"
        />
      </div>
    </div>
  );
}
